import type { InvoiceDetail } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceDetail } from './fetchInvoiceDetail';

/**
 * Generates query options for the tenant-scoped invoice detail endpoint.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional query options.
 * @returns Query options for invoice detail.
 */
export function getInvoiceDetailQueryOptions({
  invoiceId,
  options,
}: {
  invoiceId: UUID | undefined;
  options?: Omit<UseQueryOptions<InvoiceDetail>, 'queryKey' | 'queryFn'>;
}) {
  return queryOptions({
    queryFn: invoiceId
      ? (context: QueryFunctionContext) =>
          fetchInvoiceDetail(invoiceId, context)
      : skipToken,
    queryKey: queryKeysInvoice.getDetail(invoiceId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });
}

/**
 * Loads invoice detail for the v2 invoice page.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional query options.
 * @returns TanStack Query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/get_invoice_invoices__invoice_id__get
 */
export function useQueryInvoiceDetail(
  invoiceId: UUID | undefined,
  options?: Omit<UseQueryOptions<InvoiceDetail>, 'queryKey' | 'queryFn'>,
) {
  return useQuery(getInvoiceDetailQueryOptions({ invoiceId, options }));
}
